import { useState } from "react";
import type { Story } from "@/mocks/stories";
import { formatCount } from "@/utils/format";
import { catStyles, catNameMap } from "./catStyle";

export function LikeButton({
  count,
  compact = false,
}: {
  count: number;
  compact?: boolean;
}) {
  const [liked, setLiked] = useState(false);
  const [val, setVal] = useState(count);
  return (
    <button
      onClick={() => {
        setLiked((v) => !v);
        setVal((v) => (liked ? v - 1 : v + 1));
      }}
      aria-label="点赞"
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full transition-colors ${
        compact ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm"
      } ${
        liked
          ? "bg-primary-100 text-primary-700"
          : "bg-background-100 text-foreground-500 hover:text-primary-700"
      }`}
    >
      <i className={`${liked ? "ri-thumb-up-fill" : "ri-thumb-up-line"} text-base`} />
      {formatCount(val)}
    </button>
  );
}

export function CollectButton({ compact = false }: { compact?: boolean }) {
  const [saved, setSaved] = useState(false);
  return (
    <button
      onClick={() => setSaved((v) => !v)}
      aria-label="收藏"
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full transition-colors ${
        compact ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm"
      } ${
        saved
          ? "bg-accent-100 text-accent-900"
          : "bg-background-100 text-foreground-500 hover:text-accent-900"
      }`}
    >
      <i className={`${saved ? "ri-star-fill" : "ri-star-line"} text-base`} />
      {saved ? "已收藏" : "收藏"}
    </button>
  );
}

export default function StoryCard({ story }: { story: Story }) {
  const st = catStyles[story.category];
  const [read, setRead] = useState(false);
  return (
    <article className="group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-colors hover:border-primary-200 hover:bg-background-100/70">
      <button
        onClick={() => setRead((v) => !v)}
        className="flex flex-col gap-3 p-5 text-left"
      >
        <div className="flex items-center justify-between">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${st.badge}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />
            {catNameMap[story.category]}
          </span>
          <span className="text-2xl">{story.reaction}</span>
        </div>

        <h3 className="font-heading text-lg leading-snug text-foreground-900 transition-colors group-hover:text-primary-700">
          {story.title}
        </h3>
        <p className="line-clamp-3 text-sm leading-relaxed text-foreground-600">
          {read ? story.content.join("") : story.excerpt}
        </p>

        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          {story.tags.map((t) => (
            <span
              key={t}
              className="whitespace-nowrap rounded-md bg-background-200/70 px-2 py-0.5 text-xs text-foreground-500"
            >
              #{t}
            </span>
          ))}
        </div>
      </button>

      <div className="mt-auto flex items-center justify-between border-t border-background-200 px-4 py-3">
        <span className="text-xs text-foreground-500">
          <i className="ri-eye-line mr-1" />
          {formatCount(story.views)} 浏览
        </span>
        <div className="flex items-center gap-1.5">
          <span className="flex items-center gap-1 rounded-full bg-background-100 px-2.5 py-1 text-xs text-foreground-500">
            <i className="ri-chat-3-line text-base" /> {formatCount(story.comments)}
          </span>
          <CollectButton compact />
          <LikeButton count={story.likes} compact />
        </div>
      </div>
    </article>
  );
}